import { Button } from '@/components/ui/Button';
import { InlineNotice } from '@/editor/OfflineNotice';

/**
 * PwaUpdatePrompt — the "a new version is available, reload to get it" banner.
 *
 * Pure presentational: it knows nothing about the service worker. The
 * container (PwaUpdater) owns the SW registration + the `needRefresh` state
 * and passes the wired callbacks in. Keeping the component props-only means
 * it tests + stories without mocking the `virtual:pwa-register/react` module.
 *
 * Consumes the design system directly: the persistent-banner-the-user-acts-on
 * register is `InlineNotice` (system tone — this is informational and
 * recoverable, not a failure), rendered with its `actions` slot so it reads as
 * a two-column banner — glyph + message on the left, the `Button` actions
 * right-aligned and centered on one line. No ad-hoc colours or spacing —
 * positioning and rhythm come from existing tokens and the same fixed/z-50
 * floating-chrome convention the dialog/tooltip primitives use, so the prompt
 * floats above app content without entering the document flow.
 */

export interface PwaUpdatePromptProps {
  /** A new service worker is waiting → show the banner. */
  show: boolean;
  /** Activate the waiting service worker and reload into the new build. */
  onReload: () => void;
  /** Dismiss the prompt without reloading. */
  onDismiss: () => void;
}

export function PwaUpdatePrompt({ show, onReload, onDismiss }: PwaUpdatePromptProps) {
  if (!show) return null;

  return (
    <div
      // Fixed near the bottom centre, above content. Constrained width so the
      // banner reads as a focused affordance on wide viewports; the page-x
      // spacing token keeps it off the edges on narrow ones. z-50 matches the
      // floating-chrome layer (dialogs, tooltips, popovers).
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-(--space-page-x) pb-(--space-page-y)"
    >
      <InlineNotice
        tone="status"
        className="w-full max-w-lg"
        data-testid="pwa-update-prompt"
        actions={
          <>
            <Button type="button" variant="ghost" size="sm" onClick={onDismiss}>
              Dismiss
            </Button>
            <Button type="button" size="sm" onClick={onReload}>
              Reload
            </Button>
          </>
        }
      >
        A new version is available.
      </InlineNotice>
    </div>
  );
}
